import { parseSmartAdd } from '@portfolio-pm/smart-add';
import {
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getDocStore } from '../../store/doc-store.ts';
import { ToolbarGlyph, type ToolbarGlyphName } from './workspace/toolbar.tsx';

export interface QuickAddProps {
  projectName?: string;
}

type SmartAddOperator = '!' | '~' | '^' | '*' | '=' | '#' | '@' | '+' | '//' | '>' | '/' | '%';

interface SmartAddTrigger {
  operator: SmartAddOperator;
  start: number;
  end: number;
  query: string;
}

interface SmartAddSuggestion {
  id: string;
  label: string;
  value: string;
  hint?: string;
  clearOperator?: boolean;
}

interface SmartAddContext {
  locations: string[];
  owners: string[];
  projects: string[];
  tags: string[];
}

const SMART_ADD_SHORTCUTS: Array<{ operator: SmartAddOperator; label: string }> = [
  { operator: '!', label: 'Priority' },
  { operator: '~', label: 'Start Date' },
  { operator: '^', label: 'Due Date' },
  { operator: '*', label: 'Repeat' },
  { operator: '=', label: 'Estimate' },
  { operator: '#', label: 'List/Tags' },
  { operator: '@', label: 'Location' },
  { operator: '+', label: 'Give to' },
  { operator: '//', label: 'Note' },
];

const MOBILE_SMART_ADD_ACTIONS: Array<{
  operator: SmartAddOperator;
  label: string;
  glyph: ToolbarGlyphName;
  singleEntryField?:
    | 'daypart'
    | 'dueAt'
    | 'estimateMinutes'
    | 'location'
    | 'notes'
    | 'owner'
    | 'priority'
    | 'projectName'
    | 'recurrence'
    | 'scheduledAt';
}> = [
  { operator: '^', label: 'Due', glyph: 'due', singleEntryField: 'dueAt' },
  { operator: '~', label: 'Start', glyph: 'scheduled', singleEntryField: 'scheduledAt' },
  { operator: '!', label: 'Priority', glyph: 'priority', singleEntryField: 'priority' },
  { operator: '*', label: 'Repeat', glyph: 'repeat', singleEntryField: 'recurrence' },
  { operator: '#', label: 'Tags', glyph: 'tags' },
  { operator: '>', label: 'Project', glyph: 'project', singleEntryField: 'projectName' },
  { operator: '=', label: 'Estimate', glyph: 'estimate', singleEntryField: 'estimateMinutes' },
  { operator: '/', label: 'Location', glyph: 'location', singleEntryField: 'location' },
  { operator: '+', label: 'Owner', glyph: 'owner', singleEntryField: 'owner' },
  { operator: '%', label: 'When', glyph: 'daypart', singleEntryField: 'daypart' },
  { operator: '@', label: 'Place', glyph: 'location', singleEntryField: 'location' },
  { operator: '//', label: 'Note', glyph: 'note', singleEntryField: 'notes' },
];

const PRIORITY_SUGGESTIONS: SmartAddSuggestion[] = [
  { id: 'p1', label: 'P1', value: '1', hint: 'Priority 1' },
  { id: 'p2', label: 'P2', value: '2', hint: 'Priority 2' },
  { id: 'p3', label: 'P3', value: '3', hint: 'Priority 3' },
  { id: 'p0', label: 'None', value: '0', hint: 'No priority' },
];

const RECURRENCE_SUGGESTIONS: SmartAddSuggestion[] = [
  { id: 'every-day', label: 'Every day', value: 'every day' },
  { id: 'every-week', label: 'Every week', value: 'every week' },
  { id: 'every-month', label: 'Every month', value: 'every month' },
  { id: 'every-year', label: 'Every year', value: 'every year' },
  { id: 'after-day', label: 'After a day', value: 'after a day' },
  { id: 'after-week', label: 'After a week', value: 'after a week' },
  { id: 'after-month', label: 'After a month', value: 'after a month' },
  { id: 'after-year', label: 'After a year', value: 'after a year' },
  { id: 'none', label: 'No repeat', value: '', clearOperator: true },
  { id: 'advanced', label: 'Advanced...', value: 'every ' },
];

const ESTIMATE_SUGGESTIONS: SmartAddSuggestion[] = [
  { id: '15m', label: '15 min', value: '15m' },
  { id: '30m', label: '30 min', value: '30m' },
  { id: '45m', label: '45 min', value: '45m' },
  { id: '1h', label: '1 hour', value: '1h' },
  { id: '2h', label: '2 hours', value: '2h' },
];

const LOCATION_FALLBACKS = ['home', 'office', 'town', 'online'];
const OWNER_FALLBACKS = ['me', 'pat', 'team'];
const DAYPART_SUGGESTIONS: SmartAddSuggestion[] = [
  { id: 'morning', label: 'Morning', value: 'morning' },
  { id: 'afternoon', label: 'Afternoon', value: 'afternoon' },
  { id: 'evening', label: 'Evening', value: 'evening' },
  { id: 'night', label: 'Night', value: 'night' },
];
const ENERGY_SUGGESTIONS: SmartAddSuggestion[] = [
  { id: 'low', label: 'Low', value: 'low' },
  { id: 'med', label: 'Medium', value: 'med' },
  { id: 'high', label: 'High', value: 'high' },
];

export function QuickAdd({ projectName }: QuickAddProps): JSX.Element {
  const store = getDocStore();
  const tags = store.useTags();
  const projects = store.useProjects();
  const snapshot = store.useFocusSnapshot();
  const [value, setValue] = useState('');
  const [caret, setCaret] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const trigger = getSmartAddTrigger(value, caret);
  const smartAddContext = useSmartAddContext({
    projects: projects.map((project) => project.name),
    snapshotTasks: snapshot.tasks,
    tags: tags.map(({ tag }) => tag),
  });
  const suggestions = useMemo(
    () => (trigger ? buildSmartAddSuggestions(trigger, smartAddContext) : []),
    [smartAddContext, trigger],
  );
  const visibleSuggestions = suggestions.slice(0, 10);

  const updateCaret = (nextValue = value): void => {
    const position = inputRef.current?.selectionStart ?? nextValue.length;
    setCaret(position);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!value.trim()) return;
    const created = store.createTaskFromSmartAdd(value);
    if (created) {
      setValue('');
      setCaret(0);
      setSelectedIndex(0);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValue(event.target.value);
    setCaret(event.target.selectionStart ?? event.target.value.length);
    setSelectedIndex(0);
  };

  const applySuggestion = (suggestion: SmartAddSuggestion): void => {
    if (!trigger) return;
    const replacement = suggestion.clearOperator ? '' : `${trigger.operator}${suggestion.value} `;
    const nextValue = `${value.slice(0, trigger.start)}${replacement}${value.slice(trigger.end)}`;
    const nextCaret = trigger.start + replacement.length;
    setValue(nextValue);
    setCaret(nextCaret);
    setSelectedIndex(0);
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (visibleSuggestions.length > 0 && trigger) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((index) => (index + 1) % visibleSuggestions.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex(
          (index) => (index - 1 + visibleSuggestions.length) % visibleSuggestions.length,
        );
        return;
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        const suggestion = visibleSuggestions[selectedIndex] ?? visibleSuggestions[0];
        if (suggestion) applySuggestion(suggestion);
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const suggestion = visibleSuggestions[selectedIndex] ?? visibleSuggestions[0];
        if (suggestion) applySuggestion(suggestion);
        return;
      }
      if (event.key === 'Escape') {
        setCaret(-1);
        return;
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="relative">
      <label htmlFor="quick-add-input" className="sr-only">
        Add a task
      </label>
      <input
        ref={inputRef}
        id="quick-add-input"
        type="text"
        value={value}
        onChange={handleChange}
        onClick={() => updateCaret()}
        onKeyDown={handleKeyDown}
        onKeyUp={() => updateCaret()}
        onFocus={() => updateCaret()}
        placeholder={projectName ? `Add a task to ${projectName}` : 'Add a task'}
        className="ui-input"
        autoComplete="off"
      />
      {visibleSuggestions.length > 0 && trigger && (
        <SmartAddSuggestionMenu
          operator={trigger.operator}
          suggestions={visibleSuggestions}
          selectedIndex={selectedIndex}
          onSelect={applySuggestion}
        />
      )}
    </form>
  );
}

export function MobileAddTaskScreen({
  open,
  projectName,
  onClose,
}: QuickAddProps & {
  open: boolean;
  onClose(): void;
}): JSX.Element | null {
  const store = getDocStore();
  const tags = store.useTags();
  const projects = store.useProjects();
  const snapshot = store.useFocusSnapshot();
  const [value, setValue] = useState('');
  const [caret, setCaret] = useState(0);
  const [editingStart, setEditingStart] = useState<number>();
  const inputRef = useRef<HTMLInputElement>(null);
  const hasTaskText = value.trim().length > 0;
  const parsedValue = useMemo(() => parseSmartAdd(value), [value]);
  const smartAddContext = useSmartAddContext({
    projects: projects.map((project) => project.name),
    snapshotTasks: snapshot.tasks,
    tags: tags.map(({ tag }) => tag),
  });
  const activeTrigger = useMemo(() => {
    if (editingStart !== undefined && caret >= editingStart) {
      const operator = operatorAt(value, editingStart);
      if (operator) {
        return {
          operator,
          start: editingStart,
          end: caret,
          query: value.slice(editingStart + operator.length, caret),
        };
      }
    }
    return getSmartAddTrigger(value, caret);
  }, [caret, editingStart, value]);
  const suggestions = useMemo(() => {
    if (!activeTrigger) return [];
    const typed = buildMobileTypedSuggestion(activeTrigger);
    const options = buildSmartAddSuggestions(activeTrigger, smartAddContext);
    return (typed ? [typed, ...options] : options).slice(0, 8);
  }, [activeTrigger, smartAddContext]);

  useEffect(() => {
    if (!open) return;
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  if (!open) return null;

  const insertOperator = (operator: SmartAddOperator): void => {
    const action = MOBILE_SMART_ADD_ACTIONS.find((candidate) => candidate.operator === operator);
    if (action && isMobileSmartAddActionDisabled(action, parsedValue)) return;
    const before = value.slice(0, caret);
    const after = value.slice(caret);
    const needsLeadingSpace = before.length > 0 && !/\s$/.test(before);
    const insertion = `${needsLeadingSpace ? ' ' : ''}${operator}`;
    const nextValue = `${before}${insertion}${after}`;
    const nextCaret = before.length + insertion.length;
    setValue(nextValue);
    setCaret(nextCaret);
    setEditingStart(before.length + (needsLeadingSpace ? 1 : 0));
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const applySuggestion = (suggestion: SmartAddSuggestion): void => {
    if (!activeTrigger) return;
    const replacement = suggestion.clearOperator
      ? ''
      : `${activeTrigger.operator}${suggestion.value} `;
    const nextValue = `${value.slice(0, activeTrigger.start)}${replacement}${value.slice(
      activeTrigger.end,
    )}`;
    const nextCaret = activeTrigger.start + replacement.length;
    setValue(nextValue);
    setCaret(nextCaret);
    setEditingStart(undefined);
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const submit = (): void => {
    if (!value.trim()) {
      onClose();
      return;
    }
    const created = store.createTaskFromSmartAdd(value);
    if (!created) return;
    setValue('');
    setCaret(0);
    setEditingStart(undefined);
    onClose();
  };

  return (
    <div
      data-testid="mobile-add-task-screen"
      className="fixed inset-0 z-[1100] flex flex-col bg-[color:var(--app-workspace)] md:hidden"
    >
      <div className="mobile-standalone-safe-top grid shrink-0 grid-cols-[3rem_minmax(0,1fr)_4.5rem] items-center gap-2 border-b border-[color:var(--app-border)] bg-[color:var(--app-mobile-bar-bg)] px-4 pb-3">
        <button
          type="button"
          data-testid="mobile-add-task-close"
          aria-label="Close add task"
          onClick={onClose}
          className="ui-button ui-button-subtle h-11 w-11 rounded-full px-0 text-2xl"
        >
          &times;
        </button>
        <h2 className="text-center text-lg font-semibold tracking-tight text-[color:var(--app-text)]">
          Add Task
        </h2>
        <button
          type="button"
          data-testid="mobile-add-task-done"
          onClick={submit}
          className={`ui-button ui-button-primary h-11 rounded-full px-4 text-sm font-semibold ${
            hasTaskText ? '' : 'invisible'
          }`}
        >
          Done
        </button>
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
        className="pane-divider-b shrink-0 px-4 py-4"
      >
        <label htmlFor="mobile-add-task-input" className="sr-only">
          Add task with Smart Add
        </label>
        <input
          ref={inputRef}
          id="mobile-add-task-input"
          data-testid="mobile-add-task-input"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setCaret(event.target.selectionStart ?? event.target.value.length);
            if (editingStart === undefined) {
              setEditingStart(
                getSmartAddTrigger(
                  event.target.value,
                  event.target.selectionStart ?? event.target.value.length,
                )?.start,
              );
            }
          }}
          onKeyDown={(event) => {
            if (activeTrigger && event.key === 'Enter') {
              event.preventDefault();
              if (suggestions[0]) applySuggestion(suggestions[0]);
            } else if (activeTrigger && event.key === 'Escape') {
              event.preventDefault();
              setEditingStart(undefined);
              setCaret(-1);
            }
          }}
          onClick={(event) => setCaret(event.currentTarget.selectionStart ?? value.length)}
          onKeyUp={(event) => {
            if (event.key !== 'Escape')
              setCaret(event.currentTarget.selectionStart ?? value.length);
          }}
          placeholder={projectName ? `Add to ${projectName}` : 'Type a task'}
          className="w-full border-0 bg-transparent px-0 py-2 text-xl text-[color:var(--app-text)] outline-none placeholder:text-[color:var(--app-text-soft)]"
          autoComplete="off"
        />
      </form>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-6">
        {hasTaskText && (
          <>
            {!activeTrigger && (
              <div
                data-testid="mobile-smart-add-shortcuts"
                className="mx-auto grid max-w-sm grid-cols-6 gap-2"
              >
                {MOBILE_SMART_ADD_ACTIONS.map((action) => {
                  const disabled = isMobileSmartAddActionDisabled(action, parsedValue);
                  return (
                    <button
                      key={action.operator}
                      type="button"
                      data-testid={`mobile-smart-add-action-${action.operator}`}
                      aria-label={`Insert ${action.label}`}
                      title={action.label}
                      disabled={disabled}
                      onClick={() => insertOperator(action.operator)}
                      className="mobile-smart-add-action"
                    >
                      <ToolbarGlyph name={action.glyph} />
                      <span className="sr-only">{action.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {activeTrigger && (
              <div
                data-testid="mobile-smart-add-suggestions"
                className="mx-auto max-w-sm rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-2"
              >
                <p className="eyebrow-label px-2 py-1">
                  {
                    MOBILE_SMART_ADD_ACTIONS.find(
                      (item) => item.operator === activeTrigger.operator,
                    )?.label
                  }
                </p>
                <div className="grid gap-1">
                  {suggestions.map((suggestion) => (
                    <button
                      key={suggestion.id}
                      type="button"
                      data-testid={`mobile-smart-add-suggestion-${suggestion.id}`}
                      onClick={() => applySuggestion(suggestion)}
                      className="flex min-h-11 w-full items-center justify-between gap-2 rounded-md px-3 text-left text-sm text-[color:var(--app-text)] transition hover:bg-[color:var(--app-control-hover)]"
                    >
                      <span className="truncate">{suggestion.label}</span>
                      {suggestion.hint && (
                        <span className="shrink-0 text-xs text-[color:var(--app-text-soft)]">
                          {suggestion.hint}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                {suggestions.length === 0 && (
                  <p className="px-3 py-2 text-sm text-[color:var(--app-text-muted)]">
                    Type a valid value to preview it.
                  </p>
                )}
                <button
                  type="button"
                  className="ui-button ui-button-subtle mt-2 w-full"
                  onClick={() =>
                    applySuggestion({
                      id: 'cancel',
                      label: 'Cancel',
                      value: '',
                      clearOperator: true,
                    })
                  }
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function buildMobileTypedSuggestion(trigger: SmartAddTrigger): SmartAddSuggestion | undefined {
  const query = trigger.query.trim();
  if (!query) return undefined;
  const parsed = parseSmartAdd(`Task ${trigger.operator}${query}`);
  if (parsed.error) return undefined;
  const action = MOBILE_SMART_ADD_ACTIONS.find((item) => item.operator === trigger.operator);
  const field = action?.singleEntryField;
  const resolved = field ? parsed[field] : parsed.tags.filter(Boolean).join(', ');
  if (resolved === undefined || resolved === '') return undefined;
  let label = query;
  if (field === 'dueAt' || field === 'scheduledAt') {
    const hasTime = field === 'dueAt' ? parsed.dueAtHasTime : parsed.scheduledAtHasTime;
    label = new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      ...(hasTime ? { timeStyle: 'short' as const } : {}),
    }).format(Number(resolved));
  } else if (field === 'estimateMinutes') {
    label = `${resolved} min`;
  } else if (field === 'priority') {
    label = resolved === 0 ? 'No priority' : `P${resolved}`;
  } else if (typeof resolved === 'string') {
    label = resolved;
  }
  return { id: 'typed-value', label, value: query, hint: 'Tap to accept' };
}

function isMobileSmartAddActionDisabled(
  action: (typeof MOBILE_SMART_ADD_ACTIONS)[number],
  parsedValue: ReturnType<typeof parseSmartAdd>,
): boolean {
  if (!action.singleEntryField || parsedValue.error) return false;
  return parsedValue[action.singleEntryField] !== undefined;
}

function SmartAddSuggestionMenu({
  operator,
  suggestions,
  selectedIndex,
  onSelect,
}: {
  operator: SmartAddOperator;
  suggestions: SmartAddSuggestion[];
  selectedIndex: number;
  onSelect(suggestion: SmartAddSuggestion): void;
}): JSX.Element {
  return (
    <div
      data-testid="smart-add-suggestions"
      className="absolute left-0 top-12 z-[1000] w-72 rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-2 shadow-lg"
    >
      <p className="eyebrow-label px-2 py-1">
        {SMART_ADD_SHORTCUTS.find((item) => item.operator === operator)?.label}
      </p>
      <div className="max-h-72 overflow-y-auto">
        {suggestions.map((suggestion, index) => (
          <button
            key={suggestion.id}
            type="button"
            data-testid={`smart-add-suggestion-${suggestion.id}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onSelect(suggestion)}
            className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm transition ${
              index === selectedIndex
                ? 'bg-[color:var(--app-accent-soft)] text-[color:var(--app-text)]'
                : 'text-[color:var(--app-text-muted)] hover:bg-white/70 hover:text-[color:var(--app-text)]'
            }`}
          >
            <span className="truncate">{suggestion.label}</span>
            {suggestion.hint && (
              <span className="text-xs text-[color:var(--app-text-soft)]">{suggestion.hint}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function buildSmartAddSuggestions(
  trigger: SmartAddTrigger,
  context: SmartAddContext,
): SmartAddSuggestion[] {
  const dateSuggestions = buildDateSuggestions();
  const allSuggestions: SmartAddSuggestion[] =
    trigger.operator === '!'
      ? PRIORITY_SUGGESTIONS
      : trigger.operator === '^' || trigger.operator === '~'
        ? dateSuggestions
        : trigger.operator === '*'
          ? RECURRENCE_SUGGESTIONS
          : trigger.operator === '='
            ? ESTIMATE_SUGGESTIONS
            : trigger.operator === '#'
              ? valuesToSuggestions(context.tags, 'tag', (tag) => `#${tag}`)
              : trigger.operator === '>'
                ? valuesToSuggestions(context.projects, 'project')
                : trigger.operator === '@' || trigger.operator === '/'
                  ? valuesToSuggestions(context.locations, 'location')
                  : trigger.operator === '+'
                    ? valuesToSuggestions(context.owners, 'owner')
                    : trigger.operator === '%'
                      ? DAYPART_SUGGESTIONS
                      : trigger.operator === '//'
                        ? [
                            { id: 'waiting', label: 'Waiting on reply', value: 'waiting on reply' },
                            { id: 'context', label: 'Add context note', value: '' },
                          ]
                        : ENERGY_SUGGESTIONS;

  const query = trigger.query.trim().toLowerCase();
  if (!query) return allSuggestions;
  return allSuggestions.filter(
    (suggestion) =>
      suggestion.label.toLowerCase().includes(query) ||
      suggestion.value.toLowerCase().includes(query),
  );
}

function useSmartAddContext({
  projects,
  snapshotTasks,
  tags,
}: {
  projects: string[];
  snapshotTasks: Array<{ location?: string | undefined; owner?: string | undefined }>;
  tags: string[];
}): SmartAddContext {
  return useMemo(
    () => ({
      locations: uniqueStrings([
        ...snapshotTasks.map((task) => task.location).filter(isNonEmptyString),
        ...LOCATION_FALLBACKS,
      ]),
      owners: uniqueStrings([
        ...snapshotTasks.map((task) => task.owner).filter(isNonEmptyString),
        ...OWNER_FALLBACKS,
      ]),
      projects,
      tags,
    }),
    [projects, snapshotTasks, tags],
  );
}

// Lazy module-level cache keyed by local day: buildDateSuggestions runs during
// render (inside suggestion useMemos) and its output only changes when the
// calendar day rolls over.
let dateSuggestionsCache: { dayStart: number; suggestions: SmartAddSuggestion[] } | undefined;

function buildDateSuggestions(): SmartAddSuggestion[] {
  const now = Date.now();
  const dayStart = new Date(now).setHours(0, 0, 0, 0);
  if (dateSuggestionsCache?.dayStart === dayStart) return dateSuggestionsCache.suggestions;
  const suggestions: SmartAddSuggestion[] = [
    { id: 'today', label: 'Today', value: 'today' },
    { id: 'tomorrow', label: 'Tomorrow', value: 'tomorrow' },
    ...[2, 3, 4].map((offset) => {
      const label = formatWeekdayOffset(now, offset);
      return { id: `day-${offset}`, label, value: label };
    }),
    { id: '1w', label: '1 week', value: '1 week' },
    { id: '2w', label: '2 weeks', value: '2 weeks' },
  ];
  dateSuggestionsCache = { dayStart, suggestions };
  return suggestions;
}

function valuesToSuggestions(
  values: string[],
  prefix: string,
  formatLabel: (value: string) => string = (value) => value,
): SmartAddSuggestion[] {
  return uniqueStrings(values).map((value) => ({
    id: `${prefix}-${value}`,
    label: formatLabel(value),
    value,
  }));
}

function getSmartAddTrigger(value: string, caret: number): SmartAddTrigger | undefined {
  if (caret < 0) return undefined;
  if (caret > 0 && /\s/.test(value[caret - 1] ?? '')) return undefined;
  for (let index = caret - 1; index >= 0; index--) {
    const operator = operatorAt(value, index);
    if (!operator) continue;
    if (!isOperatorBoundary(value, index)) continue;
    const operatorEnd = index + operator.length;
    if (caret < operatorEnd) continue;
    const query = value.slice(operatorEnd, caret);
    if (!isMultiTokenOperator(operator) && /\s/.test(query)) return undefined;
    return { operator, start: index, end: caret, query };
  }
  return undefined;
}

function operatorAt(value: string, index: number): SmartAddOperator | undefined {
  if (value.startsWith('//', index)) return '//';
  const char = value[index];
  return char === '!' ||
    char === '~' ||
    char === '^' ||
    char === '*' ||
    char === '=' ||
    char === '#' ||
    char === '@' ||
    char === '+' ||
    char === '>' ||
    char === '/' ||
    char === '%'
    ? char
    : undefined;
}

function isOperatorBoundary(value: string, index: number): boolean {
  return index === 0 || /\s/.test(value[index - 1] ?? '');
}

function isMultiTokenOperator(operator: SmartAddOperator): boolean {
  return operator === '^' || operator === '~' || operator === '*' || operator === '//';
}

function uniqueStrings(values: string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))].sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: 'base' }),
  );
}

function isNonEmptyString(value: string | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

const WEEKDAY_SHORT_FORMAT = new Intl.DateTimeFormat(undefined, { weekday: 'short' });

function formatWeekdayOffset(now: number, offset: number): string {
  const date = new Date(now);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offset);
  return WEEKDAY_SHORT_FORMAT.format(date);
}
